/** A parsed `{% if %}` condition as a nested boolean tree.
 *
 *  The backend runs Django's real `TemplateIfParser`, so what arrives is an
 *  operator tree with correct precedence — `a and not (b or c|default:1 > 2)`
 *  becomes readable instead of something you have to re-parse in your head.
 *
 *  Rendered with nested boxes rather than SVG: the tree is small, and boxes
 *  reflow inside the drawer at any width where an SVG would need panning.
 */
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { AnatomyCondition } from '@/api/notificationAnatomy';

/** Operators that read better as a joined list than as a nested pair. */
const CHAINABLE = new Set(['and', 'or']);

function isOp(c: AnatomyCondition): c is { op: string; args: AnatomyCondition[] } {
  return typeof (c as { op?: unknown }).op === 'string';
}

function isVar(c: AnatomyCondition): c is { var: string; filters?: { name: string; args: unknown[] }[] } {
  return typeof (c as { var?: unknown }).var === 'string';
}

/** Flatten `a and (b and c)` into one row of three operands. */
function chain(c: AnatomyCondition, op: string): AnatomyCondition[] {
  if (isOp(c) && c.op === op) return c.args.flatMap((a) => chain(a, op));
  return [c];
}

function Leaf({ condition }: { condition: AnatomyCondition }) {
  if (isVar(condition)) {
    const filters = condition.filters ?? [];
    return (
      <Box
        component="span"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8125rem',
          // `participant.tag_context.reseller.do_not_send_app_promo_email` has no
          // break opportunity, so normal wrapping lets it run past the panel.
          overflowWrap: 'anywhere',
          minWidth: 0,
        }}
      >
        {condition.var}
        {filters.map((f, i) => (
          <Box key={i} component="span" sx={{ color: 'text.secondary' }}>
            {`|${f.name}${f.args.length ? `:${f.args.map(String).join(',')}` : ''}`}
          </Box>
        ))}
      </Box>
    );
  }
  const lit = (condition as { lit: unknown }).lit;
  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        color: 'text.secondary',
        overflowWrap: 'anywhere',
        minWidth: 0,
      }}
    >
      {typeof lit === 'string' ? `"${lit}"` : String(lit)}
    </Box>
  );
}

function Node({ condition, depth = 0 }: { condition: AnatomyCondition; depth?: number }) {
  if (!isOp(condition)) return <Leaf condition={condition} />;

  const { op, args } = condition;

  if (op === 'not') {
    return (
      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Chip
          label="not"
          size="small"
          color="default"
          sx={{ height: 18, fontSize: '0.6875rem', flexShrink: 0, mt: '1px' }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Node condition={args[0]!} depth={depth + 1} />
        </Box>
      </Stack>
    );
  }

  if (CHAINABLE.has(op)) {
    const operands = chain(condition, op);
    return (
      <Box
        sx={{
          borderLeft: 2,
          borderColor: 'divider',
          pl: 1,
          ml: depth === 0 ? 0 : 0.5,
          minWidth: 0,
        }}
      >
        {operands.map((operand, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={0.75}
            alignItems="flex-start"
            sx={{ py: 0.25, minWidth: 0 }}
          >
            <Typography
              variant="caption"
              sx={{
                minWidth: 26,
                flexShrink: 0,
                textAlign: 'right',
                color: i === 0 ? 'transparent' : 'text.secondary',
                fontWeight: 600,
              }}
            >
              {op}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Node condition={operand} depth={depth + 1} />
            </Box>
          </Stack>
        ))}
      </Box>
    );
  }

  // A comparison: two operands read as one line.
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="baseline"
      flexWrap="wrap"
      useFlexGap
      sx={{ minWidth: 0 }}
    >
      <Node condition={args[0]!} depth={depth + 1} />
      <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
        {op}
      </Typography>
      {args[1] !== undefined && <Node condition={args[1]} depth={depth + 1} />}
    </Stack>
  );
}

interface Props {
  condition: AnatomyCondition | null | undefined;
  /** Shown when the parser rejected the expression (Django would too). */
  raw?: string;
}

export function ConditionTree({ condition, raw }: Props) {
  if (condition === undefined) {
    return (
      <Typography variant="caption" color="text.secondary">
        Condition not available.
      </Typography>
    );
  }
  if (condition === null) {
    // parse_condition returned null: Django's own if-parser rejected it. The
    // raw text is all we can honestly show.
    return (
      <Box>
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
          Django could not parse this condition — showing the raw source.
        </Typography>
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
          }}
        >
          {raw || '(empty)'}
        </Box>
      </Box>
    );
  }
  return <Node condition={condition} />;
}
